import { FC } from 'react'

import { Box, Skeleton } from '@mui/material'

import { MotivationDiagnosisSkeleton } from '../MotivationDiagnosisSkeleton/MotivationDiagnosisSkeleton'
import { MotivationsPanelSkeleton } from '../MotivationsPanelSkeleton/MotivationsPanelSkeleton'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

export type GamePageTabSkeleton = 'overview' | 'motivations' | 'creative-ads' | 'creative-tags' | 'story-patterns'

interface GamePageBodySkeletonProps {
  activeTab: GamePageTabSkeleton
}

/**
 * Body placeholder for the `UAInsightsGamePage` tabs. Renders a layout-
 * matched skeleton for the currently active tab so the page does not look
 * blank between navigation and the first response. The sticky header renders
 * its own inline skeleton placeholders (see `UAInsightsGamePage`).
 */
export const GamePageBodySkeleton: FC<GamePageBodySkeletonProps> = ({ activeTab }) => {
  if (activeTab === 'overview') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
        {[0, 1, 2].map((i) => (
          <SectionCard key={i} title="">
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 1 }} />
          </SectionCard>
        ))}
      </Box>
    )
  }

  if (activeTab === 'motivations') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionCard title="">
          <MotivationsPanelSkeleton />
        </SectionCard>
        <SectionCard title="">
          <MotivationDiagnosisSkeleton />
        </SectionCard>
      </Box>
    )
  }

  if (activeTab === 'creative-ads') {
    return <TableSkeleton rows={10} columns={6} />
  }

  if (activeTab === 'story-patterns') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={96} sx={{ flex: 1, borderRadius: 1 }} />
          ))}
        </Box>
        <TableSkeleton rows={8} columns={6} />
      </Box>
    )
  }

  return <TableSkeleton rows={10} columns={5} />
}
